import { MapPin, X, Plus, Building2, Layers as LayersIcon, Ruler, Loader2, AlertTriangle, Box, Move3d, FileText as Parcel } from 'lucide-react';

/**
 * ExplorerPanel — Sprint Layers Phase 2 (Feb 2026).
 *
 * Right-side immersive geo-explorer panel that opens when the user picks an
 * address from the search bar. Replaces the small `LocationDropPinCard`
 * banner with a full split-view (Portfolio is temporarily closed). Shows :
 *  - Resolved address & lat/lon
 *  - Detected building footprint (area m², floors, type) from OSM Overpass
 *  - "Mode Inspection 3D" toggle → extrudes the cadastral polygon volumetrically
 *  - Projected-height slider (10-200 m) to simulate target gabarit
 *  - Gold CTA "Créer un nouveau projet ici"
 *  - Close button → clears pin, restores Portfolio
 *
 * Props:
 *  - pin: { lat, lon, display_name } | null
 *  - building: { found, polygon, area_m2, levels, height_m, building_type, name, osm_id } | null
 *  - loading: bool
 *  - inspectionMode: bool
 *  - onToggleInspection: (next: bool) => void
 *  - projectedHeight: number | null     // current slider value in meters
 *  - onProjectedHeightChange: (m: number) => void
 *  - onCreateProject: () => void
 *  - onClose: () => void
 */
const ExplorerPanel = ({
  pin,
  building,
  loading,
  inspectionMode = false,
  onToggleInspection,
  projectedHeight = null,
  onProjectedHeightChange,
  parcelle = null,
  onCreateProject,
  onClose,
}) => {
  if (!pin) return null;

  const title = (pin.display_name || '').split(',')[0]?.trim() || 'Position sélectionnée';
  const subtitle = (pin.display_name || '').split(',').slice(1, 4).join(',').trim();

  const stats = [];
  if (building?.found) {
    if (building.area_m2 != null) stats.push({ icon: Ruler, label: 'Emprise au sol', value: `${building.area_m2.toLocaleString('fr-FR')} m²` });
    if (building.levels != null) stats.push({ icon: LayersIcon, label: 'Étages', value: `${building.levels}` });
    if (building.height_m != null) stats.push({ icon: Building2, label: 'Hauteur', value: `${building.height_m} m` });
    if (building.building_type) stats.push({ icon: Building2, label: 'Type', value: building.building_type });
  }

  // Heuristic floor estimate when OSM didn't tag it : 1 floor ≈ 3 m HSP
  const estimatedLevels = (!building?.levels && building?.height_m)
    ? Math.max(1, Math.round(building.height_m / 3))
    : null;

  return (
    <aside
      className="hub-fade-in flex flex-col shrink-0 absolute top-0 right-0 bottom-0 z-[465] w-full sm:w-[360px] xl:w-[400px]"
      data-testid="explorer-panel"
      style={{
        background: 'rgba(10,10,13,0.94)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        borderLeft: '1px solid rgba(217,160,91,0.32)',
        boxShadow: '-16px 0 40px rgba(0,0,0,0.5)',
      }}
    >
      {/* Header */}
      <div
        className="px-4 py-3 flex items-center justify-between"
        style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}
      >
        <div className="flex items-center gap-2 min-w-0">
          <div
            className="w-7 h-7 rounded-lg shrink-0 flex items-center justify-center"
            style={{ background: 'rgba(217,160,91,0.18)', border: '1px solid rgba(217,160,91,0.32)' }}
          >
            <MapPin className="w-3.5 h-3.5" style={{ color: 'var(--c-gold)' }} />
          </div>
          <span
            className="text-[10px] uppercase tracking-[0.2em] font-semibold truncate"
            style={{ color: 'var(--c-gold)', fontFamily: 'Outfit, sans-serif' }}
          >
            Explorer · Site
          </span>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="w-7 h-7 rounded-md flex items-center justify-center text-white/55 hover:text-white hover:bg-white/[0.06] transition-colors"
          data-testid="explorer-panel-close"
          aria-label="Fermer"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Address block */}
      <div className="px-4 py-4" style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
        <p
          className="text-[15px] font-semibold leading-tight"
          style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--c-text, #E9E9EE)' }}
          data-testid="explorer-panel-title"
        >
          {building?.found && building?.name ? building.name : title}
        </p>
        {subtitle && (
          <p className="text-[11.5px] mt-1 leading-snug" style={{ color: 'rgba(255,255,255,0.55)' }}>
            {subtitle}
          </p>
        )}
        <p
          className="text-[10.5px] mt-2 font-mono tracking-wide"
          style={{ color: 'rgba(255,255,255,0.38)' }}
          data-testid="explorer-panel-coords"
        >
          {pin.lat.toFixed(5)}, {pin.lon.toFixed(5)}
        </p>
      </div>

      {/* Building data */}
      <div className="flex-1 overflow-y-auto px-4 py-4">
        <div className="text-[10px] uppercase tracking-[0.18em] font-semibold mb-3"
          style={{ color: 'rgba(255,255,255,0.45)' }}
        >
          Données géospatiales
        </div>

        {loading && (
          <div
            className="flex items-center gap-2.5 px-3 py-3 rounded-lg"
            style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}
            data-testid="explorer-panel-loading"
          >
            <Loader2 className="w-4 h-4 animate-spin" style={{ color: 'var(--c-gold)' }} />
            <span className="text-[12px]" style={{ color: 'rgba(255,255,255,0.75)' }}>
              Analyse de l'emprise cadastrale…
            </span>
          </div>
        )}

        {!loading && building?.found && stats.length > 0 && (
          <div className="grid grid-cols-2 gap-2" data-testid="explorer-panel-stats">
            {stats.map((s, i) => {
              const Icon = s.icon;
              return (
                <div
                  key={i}
                  className="px-3 py-2.5 rounded-lg"
                  style={{
                    background: 'rgba(217,160,91,0.07)',
                    border: '1px solid rgba(217,160,91,0.20)',
                  }}
                >
                  <div className="flex items-center gap-1.5 mb-1">
                    <Icon className="w-3 h-3" style={{ color: 'var(--c-gold)' }} />
                    <span className="text-[9.5px] uppercase tracking-wider font-semibold"
                      style={{ color: 'rgba(255,255,255,0.55)' }}
                    >
                      {s.label}
                    </span>
                  </div>
                  <p className="text-[14px] font-semibold leading-none"
                    style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--c-text, #E9E9EE)' }}
                  >
                    {s.value}
                  </p>
                </div>
              );
            })}
          </div>
        )}

        {!loading && building?.found && estimatedLevels != null && building.levels == null && (
          <div
            className="mt-3 px-3 py-2 rounded-lg text-[10.5px] flex items-start gap-2"
            style={{
              background: 'rgba(147,197,253,0.07)',
              border: '1px solid rgba(147,197,253,0.20)',
              color: 'rgba(255,255,255,0.7)',
            }}
            data-testid="explorer-panel-estimate"
          >
            <Building2 className="w-3.5 h-3.5 shrink-0 mt-0.5" style={{ color: '#93C5FD' }} />
            <span>
              <strong style={{ color: '#93C5FD' }}>Estimation ~{estimatedLevels} étages</strong>
              {' '}(basé sur hauteur ÷ 3 m HSP)
            </span>
          </div>
        )}

        {!loading && building?.found && (
          <button
            type="button"
            onClick={() => onToggleInspection?.(!inspectionMode)}
            data-testid="explorer-panel-inspection-toggle"
            aria-pressed={inspectionMode}
            className="mt-4 w-full px-3 py-2.5 rounded-lg flex items-center gap-2.5 transition-all text-left"
            style={{
              background: inspectionMode ? 'rgba(217,160,91,0.14)' : 'rgba(255,255,255,0.03)',
              border: `1px solid ${inspectionMode ? 'rgba(217,160,91,0.45)' : 'rgba(255,255,255,0.10)'}`,
              boxShadow: inspectionMode ? '0 4px 14px -4px rgba(217,160,91,0.30)' : 'none',
            }}
          >
            <div
              className="w-8 h-8 rounded-md shrink-0 flex items-center justify-center"
              style={{
                background: inspectionMode ? 'rgba(217,160,91,0.22)' : 'rgba(255,255,255,0.04)',
                border: `1px solid ${inspectionMode ? 'rgba(217,160,91,0.40)' : 'rgba(255,255,255,0.08)'}`,
              }}
            >
              <Box className="w-4 h-4" style={{ color: inspectionMode ? 'var(--c-gold)' : 'rgba(255,255,255,0.55)' }} />
            </div>
            <span className="flex-1 min-w-0">
              <span
                className="block text-[12px] font-semibold leading-tight"
                style={{ color: inspectionMode ? 'var(--c-gold)' : 'var(--c-text, #E9E9EE)' }}
              >
                Mode Inspection 3D
              </span>
              <span className="block text-[10.5px] mt-0.5" style={{ color: 'rgba(255,255,255,0.45)' }}>
                {inspectionMode ? 'Volume extrudé actif' : 'Élève l\'emprise en boîte 3D dorée'}
              </span>
            </span>
            <span
              className="shrink-0 w-9 h-5 rounded-full relative transition-colors"
              style={{
                background: inspectionMode ? 'var(--c-gold)' : 'rgba(255,255,255,0.16)',
              }}
            >
              <span
                className="absolute top-0.5 w-4 h-4 rounded-full transition-transform"
                style={{
                  left: '2px',
                  transform: inspectionMode ? 'translateX(16px)' : 'translateX(0)',
                  background: inspectionMode ? '#0E0E10' : '#F5F5F5',
                }}
              />
            </span>
          </button>
        )}

        {!loading && building?.found && inspectionMode && (
          <div
            className="mt-3 px-3 py-3 rounded-lg"
            style={{
              background: 'rgba(217,160,91,0.06)',
              border: '1px solid rgba(217,160,91,0.22)',
            }}
            data-testid="explorer-panel-height-slider-block"
          >
            <div className="flex items-center justify-between mb-2.5">
              <div className="flex items-center gap-1.5">
                <Move3d className="w-3 h-3" style={{ color: 'var(--c-gold)' }} />
                <span className="text-[10px] uppercase tracking-[0.18em] font-semibold"
                  style={{ color: 'rgba(255,255,255,0.55)' }}
                >
                  Hauteur projetée
                </span>
              </div>
              <span
                className="text-[13px] font-semibold tabular-nums"
                style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--c-gold)' }}
                data-testid="explorer-panel-height-value"
              >
                {Math.round(projectedHeight ?? building?.height_m ?? 12)} m
              </span>
            </div>
            <input
              type="range"
              min="10"
              max="200"
              step="1"
              value={Math.round(projectedHeight ?? building?.height_m ?? 12)}
              onChange={(e) => onProjectedHeightChange?.(Number(e.target.value))}
              data-testid="explorer-panel-height-slider"
              className="w-full appearance-none h-1.5 rounded-full cursor-pointer"
              style={{
                background: 'linear-gradient(to right, rgba(217,160,91,0.55), rgba(217,160,91,0.18))',
                accentColor: '#D9A05B',
              }}
            />
            <div className="flex justify-between mt-1.5 text-[9.5px]" style={{ color: 'rgba(255,255,255,0.40)' }}>
              <span>10 m</span>
              <span>≈ {Math.max(1, Math.round((projectedHeight ?? building?.height_m ?? 12) / 3))} étages</span>
              <span>200 m</span>
            </div>
            {building?.height_m && projectedHeight != null && Math.abs(projectedHeight - building.height_m) > 1 && (
              <button
                type="button"
                onClick={() => onProjectedHeightChange?.(building.height_m)}
                data-testid="explorer-panel-height-reset"
                className="mt-2 text-[10px] underline"
                style={{ color: 'rgba(255,255,255,0.55)' }}
              >
                Réinitialiser ({Math.round(building.height_m)} m OSM)
              </button>
            )}
          </div>
        )}

        {!loading && !building?.found && (
          <div
            className="px-3 py-3 rounded-lg flex items-start gap-2.5"
            style={{
              background: 'rgba(253,224,71,0.06)',
              border: '1px solid rgba(253,224,71,0.22)',
            }}
            data-testid="explorer-panel-no-building"
          >
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" style={{ color: '#FDE68A' }} />
            <span className="text-[11.5px] leading-relaxed" style={{ color: 'rgba(255,255,255,0.75)' }}>
              Aucun bâtiment référencé OSM à proximité immédiate. Vous pouvez
              tout de même créer un projet en ajustant l'emplacement par un
              clic sur la carte.
            </span>
          </div>
        )}

        {parcelle && (
          <div className="mt-4" data-testid="explorer-panel-parcelle">
            <div className="text-[10px] uppercase tracking-[0.18em] font-semibold mb-2 flex items-center gap-1.5"
              style={{ color: 'rgba(255,255,255,0.45)' }}
            >
              <Parcel className="w-3 h-3" />
              Parcelle cadastrale
            </div>
            <div
              className="px-3 py-2.5 rounded-lg"
              style={{
                background: 'rgba(217,160,91,0.08)',
                border: '1px solid rgba(217,160,91,0.30)',
              }}
            >
              <div className="flex items-center justify-between mb-1">
                <p className="text-[12.5px] font-semibold tracking-tight"
                  style={{ fontFamily: 'Outfit, sans-serif', color: '#FDE68A' }}
                >
                  {parcelle.commune || '—'} · {parcelle.section} {parcelle.numero}
                </p>
                <span className="text-[10px] font-semibold tabular-nums"
                  style={{ color: 'var(--c-gold)' }}
                  data-testid="explorer-panel-parcelle-area"
                >
                  {(parcelle.contenance_m2 || parcelle.area_m2 || 0).toLocaleString('fr-FR')} m²
                </span>
              </div>
              <p className="text-[9.5px] font-mono opacity-60" style={{ color: 'rgba(255,255,255,0.55)' }}>
                {parcelle.parcelle_id}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Sticky CTA */}
      <div
        className="px-4 py-4"
        style={{
          background: 'linear-gradient(180deg, rgba(10,10,13,0) 0%, rgba(10,10,13,0.55) 100%)',
          borderTop: '1px solid rgba(217,160,91,0.18)',
        }}
      >
        <button
          type="button"
          onClick={onCreateProject}
          data-testid="explorer-panel-create-btn"
          className="w-full h-12 rounded-xl flex items-center justify-center gap-2 font-semibold tracking-wide transition-all hover:scale-[1.015] active:scale-95"
          style={{
            background: 'linear-gradient(135deg, #D9A05B 0%, #B07A35 100%)',
            color: '#0E0E10',
            fontFamily: 'Outfit, sans-serif',
            fontSize: '13.5px',
            boxShadow: '0 10px 28px -8px rgba(217,160,91,0.55), 0 0 0 1px rgba(217,160,91,0.35)',
          }}
        >
          <Plus className="w-4 h-4" strokeWidth={2.5} />
          Créer un nouveau projet ici
        </button>
        <p className="text-center text-[10px] mt-2" style={{ color: 'rgba(255,255,255,0.40)' }}>
          Clic sur la carte pour repositionner le pin
        </p>
      </div>
    </aside>
  );
};

export default ExplorerPanel;
